import { Button } from "@/components/ui/button";
import { Calendar, Clock, Timer, Users, Globe, DollarSign } from "lucide-react";
import { formatTimeDisplay } from "@/lib/utils/time";

interface ImprovedFooterProps {
  date: Date;
  startTime: string;
  minimumCharge: number;
  currency: string;
  availableLanguages: string[];
  pricePerPerson: number;
  duration: string;
  groupSize: string;
  isSoldOut?: boolean;
  onBookNow: () => void;
  className?: string;
  locale?: string;
}

const abbreviateLanguage = (language: string): string => {
  const abbreviations: Record<string, string> = {
    English: "EN",
    Swedish: "SE",
    German: "DE",
    Norwegian: "NO",
    Spanish: "ES",
    French: "FR",
  };
  return abbreviations[language] || language.slice(0, 2).toUpperCase();
};

const getMinimumPlayers = (groupSize: string): number => {
  const match = groupSize.match(/(\d+)-(\d+)/);
  if (match) {
    return parseInt(match[1]);
  }
  return 1;
};

export function ImprovedFooter({
  date,
  startTime,
  minimumCharge,
  currency,
  availableLanguages,
  pricePerPerson,
  duration,
  groupSize,
  isSoldOut = false,
  onBookNow,
  className = "",
  locale,
}: ImprovedFooterProps) {
  const minPlayers = getMinimumPlayers(groupSize);
  const showMinimumWarning = minimumCharge > minPlayers;

  return (
    <div className={`space-y-3 ${className}`}>
      {/* 2x3 Grid Layout */}
      <div className="grid grid-cols-3 gap-2 text-sm">
        {/* Row 1 */}
        <div className="flex items-center gap-1.5">
          <DollarSign className="h-4 w-4 text-muted-foreground" />
          <span className="font-semibold">
            {pricePerPerson} {currency}/pp
          </span>
        </div>
        <div className="flex items-center gap-1.5">
          <Calendar className="h-4 w-4 text-muted-foreground" />
          <span>
            {date.toLocaleDateString("en-US", {
              month: "short",
              day: "numeric",
            })}
          </span>
        </div>
        <div className="flex items-center gap-1.5">
          <Clock className="h-4 w-4 text-muted-foreground" />
          <span>{formatTimeDisplay(startTime, locale)}</span>
        </div>

        {/* Row 2 */}
        <div className="flex items-center gap-1.5">
          <Timer className="h-4 w-4 text-muted-foreground" />
          <span>{duration}</span>
        </div>
        <div className="flex items-center gap-1.5">
          <Users className="h-4 w-4 text-muted-foreground" />
          <span>{groupSize}</span>
        </div>
        <div className="flex items-center gap-1.5">
          <Globe className="h-4 w-4 text-muted-foreground" />
          <span>{availableLanguages.map(abbreviateLanguage).join("/")}</span>
        </div>
      </div>

      {/* Book Now button */}
      <Button
        className="w-full"
        size="default"
        disabled={isSoldOut}
        onClick={onBookNow}
      >
        {isSoldOut ? "Sold Out" : "Book Now"}
      </Button>

      {/* Subtle warnings */}
      <div className="text-xs text-muted-foreground space-y-0.5">
        {showMinimumWarning && <p>* Minimum {minimumCharge} players charged</p>}
        <p>* Prices include VAT</p>
      </div>
    </div>
  );
}
